/**
 * AML Workflow Templates and RSS Feed Configuration
 *
 * Pre-defined workflow templates for AML alert remediation
 * and regulatory RSS feed sources for ingestion.
 */

import {
  WorkflowTemplate,
  AMLAlertRole,
  AMLRiskLevel,
} from '../types/aml.types';

/**
 * Pre-defined workflow templates for AML alert remediation
 */
export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'STANDARD_AML_INVESTIGATION',
    name: 'Standard AML Investigation',
    description: 'Standard process for medium-risk transaction alerts',
    forRiskLevel: AMLRiskLevel.MEDIUM,
    requiresApproval: true,
    approvalRole: AMLAlertRole.COMPLIANCE,
    steps: [
      {
        id: 'step-1',
        title: 'Initial Review',
        description: 'Review transaction details and client profile',
        assignedRole: AMLAlertRole.FRONT_OFFICE,
        estimatedDuration: '2 hours',
        status: 'pending',
      },
      {
        id: 'step-2',
        title: 'Request Documentation',
        description:
          'Request source of funds or additional documentation from client',
        assignedRole: AMLAlertRole.FRONT_OFFICE,
        estimatedDuration: '24 hours',
        status: 'pending',
      },
      {
        id: 'step-3',
        title: 'Compliance Review',
        description: 'Review documentation and make recommendation',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '4 hours',
        status: 'pending',
      },
      {
        id: 'step-4',
        title: 'Decision & Documentation',
        description: 'Document findings and final decision',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '2 hours',
        status: 'pending',
      },
      {
        id: 'step-5',
        title: 'Close Alert',
        description: 'Update alert status and archive',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '30 minutes',
        status: 'pending',
      },
    ],
  },
  {
    id: 'SAR_FILING',
    name: 'SAR Filing Process',
    description: 'Process for filing Suspicious Activity Report',
    forRiskLevel: AMLRiskLevel.HIGH,
    requiresApproval: true,
    approvalRole: AMLAlertRole.LEGAL,
    steps: [
      {
        id: 'sar-1',
        title: 'Compile Evidence',
        description:
          'Gather all transaction records, communications, and documentation',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '4 hours',
        status: 'pending',
      },
      {
        id: 'sar-2',
        title: 'Draft SAR Narrative',
        description: 'Prepare detailed narrative of suspicious activity',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '4 hours',
        status: 'pending',
      },
      {
        id: 'sar-3',
        title: 'Legal Review',
        description:
          'Legal counsel reviews SAR for accuracy and completeness',
        assignedRole: AMLAlertRole.LEGAL,
        estimatedDuration: '24 hours',
        status: 'pending',
      },
      {
        id: 'sar-4',
        title: 'Management Approval',
        description: 'Senior management sign-off on SAR filing',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '4 hours',
        status: 'pending',
      },
      {
        id: 'sar-5',
        title: 'File with Authority',
        description: 'Submit SAR to relevant regulatory authority',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '2 hours',
        status: 'pending',
      },
    ],
  },
  {
    id: 'RAPID_FALSE_POSITIVE',
    name: 'Rapid False Positive Clearance',
    description: 'Streamlined process for clearing obvious false positives',
    forRiskLevel: AMLRiskLevel.LOW,
    requiresApproval: false,
    steps: [
      {
        id: 'fp-1',
        title: 'Quick Review',
        description: 'Verify transaction is clearly legitimate',
        assignedRole: AMLAlertRole.FRONT_OFFICE,
        estimatedDuration: '30 minutes',
        status: 'pending',
      },
      {
        id: 'fp-2',
        title: 'Document Reasoning',
        description: 'Record why this is a false positive',
        assignedRole: AMLAlertRole.FRONT_OFFICE,
        estimatedDuration: '15 minutes',
        status: 'pending',
      },
      {
        id: 'fp-3',
        title: 'Compliance Spot Check',
        description: 'Compliance verifies clearance is appropriate',
        assignedRole: AMLAlertRole.COMPLIANCE,
        estimatedDuration: '15 minutes',
        status: 'pending',
      },
      {
        id: 'fp-4',
        title: 'Close Alert',
        description: 'Mark as cleared false positive',
        assignedRole: AMLAlertRole.FRONT_OFFICE,
        estimatedDuration: '5 minutes',
        status: 'pending',
      },
    ],
  },
];

/**
 * Regulatory RSS feed sources for ingestion
 */
export const REGULATORY_RSS_FEEDS: {
  name: string;
  url: string;
  category: string;
}[] = [
  {
    name: 'FINMA',
    url: 'https://www.finma.ch/en/news/rss/',
    category: 'AML',
  },
  {
    name: 'MAS',
    url: 'https://www.mas.gov.sg/rss/default.aspx',
    category: 'AML',
  },
  {
    name: 'FATF',
    url: 'https://www.fatf-gafi.org/rss/fatf-gafi-en.xml',
    category: 'AML',
  },
  {
    name: 'SEC',
    url: 'https://www.sec.gov/rss/news.xml',
    category: 'Securities',
  },
  {
    name: 'FCA',
    url: 'https://www.fca.org.uk/news/rss.xml',
    category: 'Conduct',
  },
];
